import * as fs from 'node:fs/promises';
import * as path from 'node:path';
import type { Config, Logger, ModelsDevProvider, ProviderConfig } from '@wrongstack/core';
import type { DefaultModelsRegistry } from '@wrongstack/core';
import { COMPATIBLE_PRESETS, discoverOpenAICompatibleModels } from '@wrongstack/providers';

interface DiscoverCacheEntry {
  fetchedAt: string;
  provider: ModelsDevProvider;
}
type DiscoverCache = Record<string, DiscoverCacheEntry>;

/** Active API key from a ProviderConfig (mirrors the provider factory's resolver). */
function resolveKey(cfg: ProviderConfig): string | undefined {
  if (Array.isArray(cfg.apiKeys) && cfg.apiKeys.length > 0) {
    const active = cfg.activeKey
      ? cfg.apiKeys.find((k) => k.label === cfg.activeKey)
      : undefined;
    return (active ?? cfg.apiKeys[0])?.apiKey;
  }
  return cfg.apiKey && cfg.apiKey.length > 0 ? cfg.apiKey : undefined;
}

/** Providers eligible for `/v1/models` auto-discovery and their resolved base URL. */
function eligibleProviders(
  config: Config,
): Array<{ id: string; cfg: ProviderConfig; baseUrl: string; apiKey?: string | undefined }> {
  const out: Array<{
    id: string;
    cfg: ProviderConfig;
    baseUrl: string;
    apiKey?: string | undefined;
  }> = [];
  for (const [id, cfg] of Object.entries(config.providers ?? {})) {
    const preset = COMPATIBLE_PRESETS[id];
    const enabled = cfg.autoDiscoverModels ?? preset?.autoDiscover ?? false;
    if (!enabled) continue;
    const baseUrl = cfg.baseUrl ?? preset?.defaultBaseUrl;
    if (!baseUrl) continue;
    out.push({ id, cfg, baseUrl, apiKey: resolveKey(cfg) });
  }
  return out;
}

async function readCache(file: string): Promise<DiscoverCache> {
  try {
    return JSON.parse(await fs.readFile(file, 'utf8')) as DiscoverCache;
  } catch {
    return {};
  }
}

/**
 * Fetch model lists for every auto-discovery provider in the config and merge
 * them into the catalog. Best-effort end to end: a down server or missing key
 * falls back to the last cached list (any age — a local proxy being offline at
 * boot shouldn't wipe its models), and any failure is a logged no-op so boot
 * never breaks.
 *
 * Caches the most recent successful fetch per provider+baseUrl so the models
 * survive a restart while the server is briefly unavailable.
 */
export async function discoverAndMergeProviders(opts: {
  config: Config;
  registry: DefaultModelsRegistry;
  cacheDir: string;
  logger?: Logger | undefined;
  fetchImpl?: typeof fetch | undefined;
}): Promise<void> {
  const targets = eligibleProviders(opts.config);
  if (targets.length === 0) return;

  const cacheFile = path.join(opts.cacheDir, 'discovered-models-cache.json');
  const cache = await readCache(cacheFile);
  let cacheDirty = false;

  await Promise.all(
    targets.map(async ({ id, cfg, baseUrl, apiKey }) => {
      const cacheKey = `${id} ${baseUrl}`;
      const provider = await discoverOpenAICompatibleModels(id, {
        baseUrl,
        apiKey,
        headers: cfg.headers,
        providerName: id,
        fetchImpl: opts.fetchImpl,
      });
      if (provider) {
        cache[cacheKey] = { fetchedAt: new Date().toISOString(), provider };
        cacheDirty = true;
        opts.registry.mergeOverlay({ [id]: provider });
        opts.logger?.info(
          `auto-discovered ${Object.keys(provider.models).length} models for "${id}" from ${baseUrl}`,
        );
        return;
      }
      // Fetch failed — fall back to the last cached list, if any.
      const cached = cache[cacheKey];
      if (cached) {
        opts.registry.mergeOverlay({ [id]: cached.provider });
        opts.logger?.warn(
          `auto-discovery for "${id}" failed; using ${
            Object.keys(cached.provider.models).length
          } cached models from ${cached.fetchedAt}`,
        );
      } else {
        opts.logger?.warn(
          `auto-discovery for "${id}" failed and no cache available (server at ${baseUrl} unreachable?)`,
        );
      }
    }),
  );

  if (cacheDirty) {
    try {
      await fs.writeFile(cacheFile, JSON.stringify(cache), 'utf8');
    } catch {
      // best-effort cache write
    }
  }
}
